import React from 'react';
import PropTypes from 'prop-types';
import {ThemeProvider} from '@material-ui/core/styles';
import {createTheme} from '@material-ui/core/styles';
import Drawer from "@material-ui/core/Drawer";
import {withStyles} from "@material-ui/core/styles";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTimesCircle} from "@fortawesome/free-solid-svg-icons";

let styles = () => ({
    drawer: {
        left: 'auto'
    },
    drawerMobile: {
        left: 0,
        zIndex: 100
    },
    drawerPaper: {
        width: 350,
        maxWidth: '100vw',
        height: '100%',
        padding: 20,
        outline: 'none',
        marginTop: 0,
        boxSizing: 'border-box',
        overflowX: 'auto'
    },
    drawerPaperMobile: {
        width: '100%',
        height: '90%',
        outline: 'none',
        position: 'absolute',
        bottom: 0,
        borderRadius: 50
    },
    header: {
        width: '100%'
    },
    headerMobile: {
        padding: '30px 30px 0 30px'
    },
    container: {
        padding: '10px 0',
        display: 'block',
        overflowX: 'auto',
        overflowY: 'auto',
        height: `calc(100% - 120px)`
    },
    containerMobile: {
        padding: '15px',
        display: 'block',
        overflowX: 'hidden',
        overflowY: 'auto',
        height: `calc(100% - 120px)`
    }
});

class DrawerMenuRight extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            showingConfirmDialog: false
        };
    }

    componentDidMount() {
        // Add key event listeners
        document.addEventListener('keydown', this.handleKeyDown);
        
        // Auto-focus if drawer is already open on mount
        if (this.props.open) {
            this.autoFocusFirstField();
        }
    }

    componentDidUpdate(prevProps) {
        // Auto-focus first field when drawer opens
        if (this.props.open && !prevProps.open) {
            this.autoFocusFirstField();
        }
    }

    componentWillUnmount() {
        // Clean up event listener
        document.removeEventListener('keydown', this.handleKeyDown);
    }

    autoFocusFirstField = () => {
        setTimeout(() => {
            // Enhanced selectors specifically for Material-UI components
            const selectors = [
                // Material-UI TextField input elements
                '.MuiTextField-root input:not([disabled]):not([readonly])',
                '.MuiOutlinedInput-input:not([disabled]):not([readonly])',
                '.MuiInputBase-input:not([disabled]):not([readonly])',
                // Autocomplete components
                '.MuiAutocomplete-root input:not([disabled]):not([readonly])',
                // Standard input elements
                'input[type="text"]:not([disabled]):not([readonly])',
                'input[type="number"]:not([disabled]):not([readonly])',
                'input[type="email"]:not([disabled]):not([readonly])',
                'input[type="password"]:not([disabled]):not([readonly])',
                'textarea:not([disabled]):not([readonly])',
                'select:not([disabled])',
                // Fallback for any input
                'input:not([disabled]):not([readonly]):not([type="hidden"])'
            ];
            
            // Try multiple container selectors for Material-UI drawer
            const containerSelectors = [
                '.drawer-menu-right-container', // Our specific container class
                '.MuiDrawer-paper',
                '[role="presentation"]', 
                '.MuiPaper-root'
            ];
            
            let focused = false;
            for (const containerSelector of containerSelectors) {
                if (focused) break;
                
                const container = document.querySelector(containerSelector);
                
                if (container) {
                    for (const selector of selectors) {
                        const field = container.querySelector(selector);
                        
                        if (field && field.offsetParent !== null && field.style.display !== 'none' && field.style.visibility !== 'hidden') {
                            // Try focusing the field
                            field.focus();
                            
                            // For text inputs, also try to select the text
                            if (field.select && field.type !== 'number' && field.type !== 'password') {
                                field.select();
                            }
                            
                            focused = true;
                            break;
                        }
                    }
                }
            }
        }, 150); // Increased timeout for better reliability
    }

    handleKeyDown = (event) => {
        // Only handle keys when this drawer is open
        if (!this.props.open) return;

        // Handle ESC key press when confirmation dialog is open
        if (event.key === 'Escape' && this.state.showingConfirmDialog) {
            event.preventDefault();
            this.handleCancelClose();
            return;
        }

        // Handle Enter key for form submission
        if (event.key === 'Enter' && !this.state.showingConfirmDialog) {
            // Only trigger if Enter is pressed in an input field inside the drawer
            const target = event.target;
            const isInDrawer = target.closest('[role="presentation"]') || target.closest('.drawer-menu-right-container');
            const isInputField = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';
            const isAutocompleteInput = target.closest('.MuiAutocomplete-root') && target.tagName === 'INPUT';
            
            if (isInDrawer && (isInputField || isAutocompleteInput)) {
                if (this.props.disableEnterKeySubmit) {
                    return;
                }

                // Don't prevent default for autocomplete dropdowns when they're open
                const autocompleteDropdown = target.closest('.MuiAutocomplete-root');
                if (autocompleteDropdown && autocompleteDropdown.querySelector('.MuiAutocomplete-popper')) {
                    return; // Let autocomplete handle the Enter key
                }
                // Popper is often portaled to document body; still treat as open autocomplete
                if (isAutocompleteInput && document.querySelector('.MuiAutocomplete-popper')) {
                    return;
                }
                
                // Check if this is the packing slips drawer and disable Enter submission
                const drawerTitle = document.querySelector('.drawer-menu-right-title');
                if (drawerTitle && (drawerTitle.textContent.includes('Packing Slip') || drawerTitle.textContent.includes('packing slip') || drawerTitle.textContent.includes('Invoice') || drawerTitle.textContent.includes('invoice') || drawerTitle.textContent.includes('Purchase') || drawerTitle.textContent.includes('purchase'))) {
                    return; // Don't submit on Enter for packing slips drawer
                }
                
                event.preventDefault();
                this.handleEnterSubmit();
            }
        }
    }

    handleEnterSubmit = () => {
        if (this.props.onSubmit) {
            const confirmMessage = this.props.submitConfirmMessage || 'Are you sure you want to submit this form?';
            if (window.confirm(confirmMessage)) {
                this.props.onSubmit();
            }
        } else {
            // If no onSubmit prop, try to find and click the first save/submit button
            const drawer = document.querySelector('.drawer-menu-right-container');
            if (drawer) {
                const saveButton = drawer.querySelector('button[class*="success-button"], button[class*="primary"], button[onclick*="Save"], button[onclick*="save"]');
                if (saveButton && !saveButton.disabled) {
                    saveButton.click();
                }
            }
        }
    }

    handleDrawerEscapeKeyDown = (event) => {
        // Handle ESC key press on the drawer itself
        event.preventDefault();
        if (this.props.hasUnsavedChanges) {
            this.setState({ showingConfirmDialog: true });
        } else {
            this.props.onClose();
        }
    }



    handleBackdropClick = () => {
        if (this.props.hasUnsavedChanges) {
            this.setState({ showingConfirmDialog: true });
        } else {
            this.props.onClose();
        }
    }

    handleConfirmClose = () => {
        this.setState({ showingConfirmDialog: false });
        this.props.onClose();
    }

    handleCancelClose = () => {
        this.setState({ showingConfirmDialog: false });
    }

    handleCloseButtonClick = () => {
        if (this.props.hasUnsavedChanges) {
            this.setState({ showingConfirmDialog: true });
        } else {
            this.props.onClose();
        }
    }

    getDesktopPaperWidth = () => {
        const { customWidth } = this.props;
        if (customWidth == null || customWidth === '') {
            return undefined;
        }

        const widthValue = typeof customWidth === 'number' ? `${customWidth}px` : String(customWidth);
        return `min(${widthValue}, 100vw)`;
    }

    render() {
        let {classes} = this.props;

        // Create a Material-UI v4 theme
        const theme = createTheme({
            palette: {
                primary: {
                    main: '#1976d2',
                },
                secondary: {
                    main: '#dc004e',
                },
            },
        });

        return (
            <ThemeProvider theme={theme}>
                <Drawer 
                    open={this.props.open} 
                    anchor={window.innerWidth > 960 ? "right" : "bottom"}
                    className={window.innerWidth > 960 ? classes.drawer : classes.drawerMobile}
                    onEscapeKeyDown={this.handleDrawerEscapeKeyDown}
                    PaperProps={{
                        className: window.innerWidth > 960 ? classes.drawerPaper : classes.drawerPaperMobile,
                        style: {
                            ...(window.innerWidth > 960 ? {
                                maxWidth: '100vw',
                                ...(this.props.customWidth ? { width: this.getDesktopPaperWidth() } : {}),
                            } : {}),
                            zIndex: 1300
                        }
                    }}
                    BackdropProps={{
                        onClick: this.handleBackdropClick,
                        style: { 
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            zIndex: 1200
                        }
                    }}
                >
                    <div className={window.innerWidth > 960 ? classes.header : classes.headerMobile}>
                        <h2 className="drawer-menu-right-title">{this.props.title}</h2>
                        <FontAwesomeIcon 
                            icon={faTimesCircle} 
                            size="2x" 
                            className="drawer-menu-right-close" 
                            onClick={this.handleCloseButtonClick}
                        />
                        <hr style={{margin: '20px 0 10px'}}/>
                    </div>
                    <div className={`drawer-menu-right-container ${window.innerWidth > 960 ? classes.container : classes.containerMobile}`}>
                        {this.props.children}
                    </div>
                </Drawer>

                {/* Confirmation Dialog */}
                {this.state.showingConfirmDialog && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        zIndex: 1400,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    onClick={this.handleCancelClose}>
                        <div style={{
                            backgroundColor: 'white',
                            padding: '30px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                            maxWidth: '400px',
                            textAlign: 'center'
                        }}
                        onClick={(e) => e.stopPropagation()}>
                            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Unsaved Changes</h3>
                            <p style={{ marginBottom: '30px', color: '#666' }}>
                                Are you sure you want to exit without saving your changes?
                            </p>
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                                <button 
                                    onClick={this.handleCancelClose}
                                    style={{
                                        padding: '10px 20px',
                                        border: '1px solid #ccc',
                                        backgroundColor: 'white',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button 
                                    onClick={this.handleConfirmClose}
                                    style={{
                                        padding: '10px 20px',
                                        border: 'none',
                                        backgroundColor: '#ff6161',
                                        color: 'white',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Exit Without Saving
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </ThemeProvider>
        );
    }
}

DrawerMenuRight.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string,
    children: PropTypes.node,
    hasUnsavedChanges: PropTypes.bool,
    customWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onSubmit: PropTypes.func,
    submitConfirmMessage: PropTypes.string,
    /** When true, Enter in inputs does not trigger submit / click primary button (e.g. bulk category Autocomplete). */
    disableEnterKeySubmit: PropTypes.bool,
    classes: PropTypes.object.isRequired,
};

DrawerMenuRight.defaultProps = {
    hasUnsavedChanges: false,
    submitConfirmMessage: 'Are you sure you want to submit this form?',
    disableEnterKeySubmit: false,
};

DrawerMenuRight = withStyles(styles, {withTheme: true})(DrawerMenuRight);
export default DrawerMenuRight;
